<template>
  <div class="login-container">
    <div v-if="downtimeFlag" class="downtime-banner">
      Portal Services will be unavailable on 2nd October 2026 (Friday), between
      11:00AM IST to 2:00PM IST due to planned maintenance.
    </div>
    <div class="left-form">
      <template v-if="!phoneLogin">
        <img src="@/assets/images/common/drip-logo.svg" />
        <h1 class="heading">{{ $t('newLogin.heading') }}</h1>
        <p class="mb-2">{{ $t('newLogin.emailSubHeading') }}</p>
        <b-form @submit.prevent="loginWithEmail" data-vv-scope="emailForm">
          <div class="status-card-container">
            <error-card
              :error="error"
              link="/forgot-password"
              :linkText="$t('newLogin.error.linkText')"
              :text="$t('newLogin.error.text')"
              :description="$t('newLogin.error.description')"
            />
            <success-card
              :success="success"
              :text="$t('newLogin.success.text')"
              :description="$t('newLogin.success.description')"
            />
            <success-card
              :success="registrationStatus"
              :text="$t('newLogin.registration.text')"
              :description="$t('newLogin.registration.description')"
            />
            <success-card
              :success="resetPasswordStatus"
              :text="$t('newLogin.resetPassword.text')"
              :description="$t('newLogin.resetPassword.description')"
            />
          </div>
          <div class="form-container">
            <div class="form-field">
              <h3 class="label">{{ $t('newLogin.emailForm.emailLabel') }}</h3>
              <input
                type="text"
                class="input-field"
                :class="{ 'input-error': errors.first('emailForm.email') }"
                :placeholder="$t('newLogin.emailForm.emailPlaceholder')"
                v-model="formData.email"
                v-validate="'required|email'"
                data-vv-validate-on="input"
                data-vv-scope="emailForm"
                data-vv-as="Email"
                name="email"
              />
              <div
                class="error-block mt-2"
                v-if="errors.first('emailForm.email')"
              >
                <span class="error">
                  <b-icon-x-circle
                    v-if="errors.first('emailForm.email')"
                    variant="danger"
                    class="mr-1"
                  />
                  {{ errors.first('emailForm.email') }}
                </span>
              </div>
            </div>
            <div class="password-field">
              <h3 class="label">
                {{ $t('newLogin.emailForm.passwordLabel') }}
              </h3>
              <div class="input-with-icon">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  class="input-field"
                  :class="{ 'input-error': errors.first('emailForm.password') }"
                  :placeholder="$t('newLogin.emailForm.passwordPlaceholder')"
                  v-model="formData.password"
                  v-validate="'required|min:3'"
                  data-vv-validate-on="input"
                  data-vv-scope="emailForm"
                  data-vv-as="Password"
                  name="password"
                />
                <b-icon-eye
                  v-if="showPassword"
                  class="password-toggle-icon"
                  @click="showPassword = !showPassword"
                />
                <b-icon-eye-slash
                  v-else
                  class="password-toggle-icon"
                  @click="showPassword = !showPassword"
                />
              </div>
              <div
                class="mt-2 error-block"
                v-if="errors.first('emailForm.password')"
              >
                <span class="error">
                  <b-icon-x-circle
                    v-if="errors.first('emailForm.password')"
                    variant="danger"
                    class="mr-1"
                  />
                  {{ errors.first('emailForm.password') }}
                </span>
              </div>
            </div>
            <!-- checkbox -->
            <div class="checkbox d-flex justify-content-between">
              <b-form-checkbox
                id="checkbox-1"
                name="checkbox-1"
                v-model="rememberMe"
                value="true"
                unchecked-value="false"
                class="text-align-center"
              >
                {{ $t('newLogin.emailForm.checkbox') }}
              </b-form-checkbox>
              <router-link
                :to="{ name: 'forgotPassword' }"
                class="link"
                v-track-click
                id="email-form-forgot-password"
              >
                {{ $t('newLogin.emailForm.forgotPassword') }}
              </router-link>
            </div>
            <!-- edit this button -->
            <action-button
              variant="solid"
              type="submit"
              extraClass="action-button pt-3 text-center"
            >
              <b-icon-arrow-repeat
                v-if="isLoading"
                animation="spin"
                scale="0.9"
                class="mr-2"
              />
              {{ $t('newLogin.emailForm.loginButton') }}
            </action-button>
            <!-- commenting for now as we don't have clarity on sign up link -->
            <!-- <a
            href=""
            class="d-block text-center link mt-4 mb-2"
            >{{ $t('newLogin.emailForm.link1') }}
          </a> -->
            <div class="or-block mt-4">
              <div class="or-task">or</div>
              <div class="backdrop-line"></div>
            </div>
            <p class="d-block text-center info mt-4 mb-0">
              {{ $t('newLogin.emailForm.link2') }}
            </p>
            <div>
              <div class="spacing-24"></div>
              <action-button
                variant="outline"
                :click="toggleToPhone"
                extraClass="pt-3 text-center"
              >
                {{ $t('newLogin.emailForm.phoneLogin') }}
              </action-button>
            </div>
          </div>
        </b-form>
      </template>
      <phone-login
        v-else
        embedded
        @toggle="toggleToEmail"
        @resetPhone="resetPhoneLogin"
        @needs-consent="onPhoneNeedsConsent"
      />
    </div>
    <image-container />
  </div>
</template>
<script>
import Cookies from 'js-cookie'
import PhoneLogin from '@/views/Login/PhoneLogin'
import ImageContainer from '@/views/Login/ImageContainer'
import ErrorCard from '@/components/validation/ErrorCard'
import SuccessCard from '@/components/validation/SuccessCard'
import ActionButton from '@/components/common/ActionButton'
import { freshdeskMixin } from '@/mixins/Freshdesk'
import userService from '@/services/user'
import accountService from '@/services/accounts'
import { APP_TYPES } from '@/utils/constants'
import { captureException } from '@sentry/vue'
import {
  buildSubjectFromAccount,
  checkConsentRequired,
  clearConsentExempt,
  isDpdpConsentApplicable,
  markConsentCleared,
  markConsentExempt,
  subjectFromStore,
  persistAccessToken,
  executeNextNavigation
} from '@/helpers/dpdpConsent'
import { dpdpConsentRoute, handOffGate } from '@/helpers/dpdpConsentGuard'

export default {
  errorCaptured: function (err) {
    console.log('Caught error', err.message)
  },
  components: {
    ErrorCard,
    SuccessCard,
    ImageContainer,
    PhoneLogin,
    ActionButton
  },
  data() {
    return {
      pendingAuth: null,
      formData: {
        email: this.$store.state.account.email || '',
        password: ''
      },
      rememberMe: false,
      redirectToDripOne: false,
      country: '',
      success: false,
      error: false,
      registrationStatus: false,
      resetPasswordStatus: false,
      phoneLogin: false,
      isLoading: false,
      showPassword: false
    }
  },
  mounted() {
    document.title = 'Login | Drip Portal'
    this.$mpTrack('Login - page mounted')
    const rememberMe = Cookies.get('rememberMe')
    if (rememberMe === 'true') {
      this.rememberMe = true
      this.formData.email = Cookies.get('email')
    }
    if (this.$route.query.registration === 'success') {
      this.registrationStatus = true
    }
    if (this.$route.query.resetpassword === 'success') {
      this.resetPasswordStatus = true
    }
    this.clearExistingUserDetails()
    this.loadGeo()
    this.redirectToDripOne = this.decideRedirectionToDripOne()
    if (this.$route.query.token) {
      this.checkToken()
    }
    this.hideFreshdeskWidget()
    this.checkForVerification()
  },
  methods: {
    loginWithEmail() {
      this.clearExistingUserDetails()
      this.$validator
        .validateAll('emailForm')
        .then((result) => {
          if (result && !this.isLoading) {
            this.$mpTrack('Login - Login button clicked', {
              email: this.formData.email
            })
            this.isLoading = true
            Cookies.set('rememberMe', this.rememberMe)
            if (this.rememberMe) {
              Cookies.set('email', this.formData.email)
            }
            userService
              .fetchAccessToken()
              .then((response) => {
                return accountService
                  .signInAccount(response.data.token, this.formData)
                  .then((res) => {
                    if (
                      res &&
                      res.data &&
                      res.data.message &&
                      res.data.message.token
                    ) {
                      this.$mpTrack('Login - Session generated', {
                        email: this.formData.email
                      })
                      // Defer browser token persistence until after DPDP consent (RF path)
                      this.handleRedirect(res.data)
                    } else {
                      this.submitFail()
                    }
                  })
                  .catch((err) => {
                    this.throwError(err, 'session')
                  })
                  .finally(() => {
                    this.isLoading = false
                  })
              })
              .catch((err) => {
                this.throwError(err, 'token')
              })
              .finally(() => (this.isLoading = false))
          }
        })
        .catch((e) => {
          console.log('Validations', e)
        })
    },
    toggleToEmail() {
      this.$mpTrack('Login - Login with email button clicked')
      this.formData.email = ''
      this.formData.password = ''
      this.$nextTick(() => {
        this.phoneLogin = false
      })
    },
    toggleToPhone() {
      this.$mpTrack('Login - Login with phone number button clicked ')
      this.formData.email = ''
      this.formData.password = ''
      this.$nextTick(() => {
        this.phoneLogin = true
      })
    },
    checkToken() {
      this.isLoading = true
      return accountService
        .authenticateAccount(this.$route.query.token)
        .then((res) => {
          if (res && res.data && res.data.message && res.data.message.token) {
            this.$mpTrack('Login - Logged as admin', {
              user_id: this.$route.query.user_id
            })
            this.handleRedirect(res.data)
          } else {
            this.isLoading = false
            this.$notify({
              title: this.$t('error.unableToProcess'),
              type: 'error'
            })
          }
        })
        .catch((e) => {
          console.log(e)
          this.$notify({
            title: this.$t('error.unableToProcess'),
            type: 'error'
          })
        })
        .finally(() => (this.isLoading = false))
    },
    clearExistingUserDetails() {
      this.$store.dispatch('unsetLocale')
      this.$store.dispatch('unsetAccountId')
    },
    filterCallbackLink(callbackUrl) {
      if (['/logout', '/login'].includes(callbackUrl)) {
        return '/'
      }
      return callbackUrl
    },
    persistPendingAuthAndGo(next) {
      const pending = this.pendingAuth
      if (pending?.token) {
        persistAccessToken(pending.token)
      }
      // Consent was settled during this login; let the route guard skip its own
      // check for the email it derives from the stored user profile.
      markConsentCleared(subjectFromStore(this.$store).email)
      clearConsentExempt()
      this.pendingAuth = null
      if (next?.path === '/legal-sign/') {
        this.$mpTrack('Login - Redirected to RF Portal Legal', {
          email: this.formData.email
        })
      } else {
        this.$mpTrack('Login - Redirected to RF Portal', {
          link: next?.path === '/' ? 'default' : next?.path,
          email: this.formData.email
        })
      }
      executeNextNavigation(this.$router, next)
    },
    persistSkippedAndGo() {
      const next = this.pendingAuth?.next || { type: 'route', path: '/' }
      persistAccessToken(this.pendingAuth?.token)
      this.pendingAuth = null
      this.$mpTrack('Login - DPDP consent skipped for session', {
        email: this.formData.email
      })
      executeNextNavigation(this.$router, next)
    },
    // Store the session before consent, as los-portal does, so a refresh on the
    // consent page keeps the user there. The route guard blocks every other
    // signed-in screen until consent is settled.
    goToConsentPage(gate) {
      const next = this.pendingAuth?.next || { type: 'route', path: '/' }
      persistAccessToken(this.pendingAuth?.token)
      clearConsentExempt()
      handOffGate(gate)
      this.pendingAuth = null
      this.$router.push(dpdpConsentRoute({ fullPath: next.path || '/' }))
    },
    onPhoneNeedsConsent({ subject, next, token, gateData, gateFailed, jwt }) {
      this.pendingAuth = { token, next }
      this.goToConsentPage({ gateData, error: gateFailed || null, jwt })
      this.$mpTrack('Login - Showing DPDP Consent on login', {
        email: subject?.email
      })
    },
    async handleRedirect(sessionData) {
      let errorMessage = ''
      try {
        const callback = this.$route.query.callback
        const application = this.$route.query.app

        const userType = sessionData.utype
        const userTarget = sessionData.target

        const isUserTypeLegit =
          ['onboarding', 'portal'].includes(userTarget) &&
          ['channel_partner', 'super_user', 'portal_user'].includes(userType)

        this.$store.dispatch('setUserType', sessionData)
        const userResponse = await accountService.fetchLoggedUser(
          sessionData.message.token
        )
        const userData = userResponse.data

        let account = userData && userData.linked_users[0]

        if (!account && userTarget !== 'onboarding') {
          console.error('Business account not linked!')
          errorMessage = 'Business account not linked!'
          this.$notify({
            title: this.$t('error.unableToProcess'),
            type: 'error'
          })
          this.$mpTrack('Login - Business account not linked!', {
            target: sessionData.target,
            type: sessionData.utype,
            email: this.formData.email
          })
          return
        }
        if (this.$route.query.user_id) {
          userData.link_id = this.$route.query.user_id
        }
        this.$store.dispatch('setUser', userData)
        localStorage.setItem('user', JSON.stringify(userData))
        this.$mpTrack('Login Successful', {
          email: this.formData?.email || '',
          mfSessionId: window.mouseflow?.getSessionId()
        })
        let redirectTo = '/'
        if (this.redirectToDripOne) {
          redirectTo = '/insights'
        }

        // admin log in using user id params
        if (this.$route.query.user_id) {
          this.$mpTrack('Login - Logged as admin user')
          markConsentExempt()
          persistAccessToken(sessionData.message.token)
          this.$router.push({
            path: redirectTo,
            query: { user_id: this.$route.query.user_id }
          })
          return
        }

        if (!isUserTypeLegit) {
          console.error('User not authorized to login!')
          errorMessage = 'User not authorized to login!'
          this.$notify({
            title: this.$t('error.unauthorizedToolTip'),
            type: 'error'
          })
          this.$mpTrack('Login - Type not mentioned!', {
            target: sessionData.target,
            type: sessionData.utype,
            email: this.formData.email
          })
          return
        }

        const { productType, userId } = this.isProductType(
          userData,
          userTarget,
          userType
        )

        // redirects to Portal SCF
        // DPDP consent is not enforced here yet (Phase 2, see DPDP_CONSENT_PRODUCTS)
        if (productType === APP_TYPES.SCF) {
          this.$mpTrack('Login - Redirected to SCF Portal', {
            email: this.formData.email
          })
          persistAccessToken(sessionData.message.token)
          Cookies.set('user_id', userData.linked_users[0].link_id, {
            domain: '.dripcapital.com'
          })
          const redirectUrl =
            process.env.VUE_APP_DRIP_SCF_PORTAL_HOST +
            (application === APP_TYPES.PSCF ? callback : '')
          window.location.href = redirectUrl
          return
        }

        // redirects to Channel Partner
        // DPDP consent is not enforced here yet (Phase 2, see DPDP_CONSENT_PRODUCTS)
        if (productType === APP_TYPES.CP) {
          this.$mpTrack('Login - Redirected to CP Portal', {
            email: this.formData.email
          })
          Cookies.set('user_id', userId, { domain: '.dripcapital.com' })
          Cookies.set('token', sessionData.message.token, {
            domain: '.dripcapital.com'
          })
          persistAccessToken(sessionData.message.token)
          const redirectUrl =
            process.env.VUE_APP_CP_HOST +
            (application === APP_TYPES.CP ? callback : '')
          window.location.href = redirectUrl
          return
        }

        // redirects to Portal NDAJ
        // DPDP consent is not enforced here yet (Phase 2, see DPDP_CONSENT_PRODUCTS)
        if (productType === APP_TYPES.NDAJ) {
          this.$mpTrack('Login - Redirected to NDAJ Portal', {
            email: this.formData.email
          })
          Cookies.set('user_id', userId, { domain: '.dripcapital.com' })
          Cookies.set('token', sessionData.message.token, {
            domain: '.dripcapital.com'
          })
          persistAccessToken(sessionData.message.token)
          const redirectUrl =
            process.env.VUE_APP_NDAJ_HOST +
            (application === APP_TYPES.NDAJ ? callback : '')
          window.location.href = redirectUrl
          return
        }

        // RF in-app — show consent in .left-form; persist token only after accept
        let next
        if (
          account.country === 'India' &&
          account.role === 'exporters' &&
          account.status === 'approved' &&
          !account.legal_agreements_esigned
        ) {
          next = { type: 'route', path: '/legal-sign/' }
        } else if (application === APP_TYPES.RFP && callback) {
          next = { type: 'route', path: this.filterCallbackLink(callback) }
        } else {
          next = { type: 'route', path: '/' }
        }

        this.pendingAuth = {
          token: sessionData.message.token,
          next
        }

        if (
          !isDpdpConsentApplicable({
            productType: APP_TYPES.RFP,
            country: account.country
          })
        ) {
          this.persistPendingAuthAndGo(next)
          return
        }

        const subject = buildSubjectFromAccount(account, this.formData.email)
        const consentCheck = await checkConsentRequired(subject, {
          sessionToken: sessionData.message.token
        })
        if (consentCheck.required) {
          this.goToConsentPage(consentCheck)
          this.$mpTrack('Login - Showing DPDP Consent on login', {
            email: this.formData.email,
            missing_email: Boolean(consentCheck.missingEmail)
          })
          return
        }

        if (consentCheck.skipped) {
          this.persistSkippedAndGo()
          return
        }

        this.persistPendingAuthAndGo(next)
        return
      } catch (e) {
        console.log(e)
        this.$mpTrack('Login - Failed to redirect session', {
          email: this.formData.email || '',
          mfSessionId: window.mouseflow && window.mouseflow.getSessionId()
        })
        this.clearExistingUserDetails()
        captureException(new Error(e), {
          level: 'error',
          tags: {
            email: this.formData.email || '',
            target: sessionData.target,
            message: errorMessage,
            page: 'login-session-code',
            mfSessionId: window.mouseflow && window.mouseflow.getSessionId()
          }
        })
        this.$notify({
          title: this.$t('error.unableToProcess'),
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    },
    loadGeo() {
      return userService
        .fetchAccessToken()
        .then((res1) => {
          return userService.fetchGEOLocation(res1.data.token)
        })
        .then((res2) => {
          this.country = res2.data && res2.data.country
          this.$mpTrack('Country Set - Success', {
            country: this.country
          })
        })
        .catch((e) => {
          this.$mpTrack('Country Set - Failed', {
            country: this.country,
            error: e
          })
        })
    },
    decideRedirectionToDripOne() {
      const redirectSource = this.$route.path
      return redirectSource === '/insights'
    },
    submitFail() {
      this.error = true
      this.isLoading = false
      this.throwError({ message: 'Response is empty' }, 'session')
    },
    throwError(err, api) {
      console.error(err)
      const error = err?.response?.data || err?.message || err
      const trackData = {
        form: 'email',
        email: this.formData.email || 'N/A'
      }
      const trackDataMP = {
        ...trackData,
        error: error || 'No response',
        mfSessionId: window.mouseflow && window.mouseflow.getSessionId()
      }
      if (api === 'token') {
        this.$mpTrack('Login - Token generation failed', trackDataMP)
        return
      }
      if (api === 'session') {
        window._mfq.push([
          'setVariable',
          'LOGIN_FAILED',
          (trackData && trackData.email) || ''
        ])
        if (err && err.message && err.message.includes('401')) {
          trackDataMP.reason = '401 error: ' + err.message
          this.$mpTrack('Login - Failed Login', trackDataMP)
          this.clearExistingUserDetails()
          this.$notify({
            title: this.$t('error.unableToProcess'),
            type: 'error'
          })
          return
        }
        if (
          err &&
          err.response &&
          err.response.status &&
          err.response.status === 422
        ) {
          trackDataMP.reason = '422 error: credentials failed'
          this.$mpTrack('Login - Failed Login', trackDataMP)
          return this.$notify({
            title: this.$t('notifications.login.title'),
            text: this.$t('notifications.login.text'),
            type: 'error'
          })
        }
        trackDataMP.reason = 'Others: ' + err && err.message
        this.clearExistingUserDetails()
        this.$mpTrack('Login - Failed Login', trackDataMP)
        this.$notify({
          title: this.$t('error.unableToProcess'),
          type: 'error'
        })
      }
    },
    checkForVerification() {
      if (this.$route.query.emailVerified === 'true') {
        this.success = true
      } else if (this.$route.query.emailVerified === 'false') {
        this.error = true
      }
    },
    resetPhoneLogin() {
      this.phoneLogin = true
    },
    isProductType(user, userTarget, userType) {
      // for leads
      if (userTarget === 'onboarding' && userType === 'portal_user') {
        return {
          productType: APP_TYPES.NDAJ,
          userId: user.link_id
        }
      }
      // for channel partner
      if (userTarget === 'onboarding' && userType === 'channel_partner') {
        return {
          productType: APP_TYPES.CP,
          userId: user.id
        }
      }
      // for BDMs
      if (this.$store.state.account.userType === 'super_user') {
        return {
          productType: APP_TYPES.NDAJ,
          userId: user.id
        }
      }
      // for SCF users
      if (
        user.linked_users &&
        user.linked_users.length &&
        user.linked_users[0].debtor_type === 'scf'
      ) {
        return {
          productType: APP_TYPES.SCF,
          userId: user.linked_users[0].link_id
        }
      }
      return { productType: APP_TYPES.RFP, userId: '' }
    }
  },
  computed: {
    downtimeFlag() {
      const now = new Date()

      // Convert current time to IST
      const nowIST = new Date(
        now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
      )

      // Define downtime start & end (IST)
      const downtimeStart = new Date('2026-10-01T00:00:00+05:30')
      const downtimeEnd = new Date('2026-10-02T14:00:00+05:30')

      // Check if current time is in downtime
      const isDown =
        nowIST.getTime() >= downtimeStart.getTime() &&
        nowIST.getTime() <= downtimeEnd.getTime()

      if (isDown) {
        return true
      }
      return false
    },
    isDisabled() {
      return this.formData.email.length > 0 && this.formData.password.length > 0
    }
  },
  mixins: [freshdeskMixin]
}
</script>
<style scoped>
.downtime-banner {
  background: #ffc107;
  text-align: center;
  z-index: 999;
  position: fixed;
  width: 100%;
  padding: 8px 0;
  font-size: 14px;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-icon .input-field {
  padding-right: 48px;
}

.password-toggle-icon {
  position: absolute;
  right: 16px;
  cursor: pointer;
  color: #27b67c;
  width: 20px;
  height: 20px;
}

.password-toggle-icon:hover {
  color: #1f9364;
}
</style>
